import type { FC, ReactNode } from "react";
import { useMatch, useMatches, type UIMatch } from "react-router";
import z from "zod";
import { useMainNavigationFeatureFlags } from "../helpers/mainNavigation.js";
import { AppNavigation } from "./appNavigation.js";
import { appPaths } from "./appPaths.js";
import { renderWorkspaceNavigationMenu } from "./navigation/menu/workspaceNavigationMenu.js";
import { useProjectWorkspaceId } from "./navigation/projectWorkspace.js";
import { useRememberedWorkspaceId } from "./navigation/rememberedWorkspace.js";
import {
  determineDefaultWorkspaceId,
  useWorkspaceOptions,
  workspaceIdSchema,
} from "./navigation/workspaces.js";

export interface AppNavigationContainerProps {}

export const AppNavigationContainer: FC<AppNavigationContainerProps> = () => {
  const navigationMatches = useMatches().filter(checkIsMatchWithNavigation);

  // Resolve the workspace and the menu independently: the nearest match that
  // sets each wins, so a settings route can define a menu without a workspace.
  const handleWorkspaceId = navigationMatches.findLast(
    (match) => match.handle.workspaceId != null,
  )?.handle.workspaceId;
  const handleNavigationMenu = navigationMatches.findLast(
    (match) => match.handle.navigationMenu != null,
  )?.handle.navigationMenu;

  // The `/` route pins neither a workspace nor a menu: both follow the
  // feature-flag-driven default workspace, matching where HomePage redirects.
  const usesDefaultWorkspace = navigationMatches.some(
    (match) => match.handle.defaultWorkspace === true,
  );
  const flags = useMainNavigationFeatureFlags();
  const defaultWorkspaceId = determineDefaultWorkspaceId(flags);

  // Projects are a one-off: their workspace is data-driven (recruiting vs
  // training), so it can't live on the static route handle.
  const projectMatch = useMatch(`${appPaths.project.pattern}/*`);
  const projectWorkspaceId = useProjectWorkspaceId(
    projectMatch?.params.projectId,
  );
  const currentWorkspaceId = projectMatch
    ? projectWorkspaceId
    : usesDefaultWorkspace
      ? defaultWorkspaceId
      : handleWorkspaceId;

  const navigationMenu = usesDefaultWorkspace
    ? renderWorkspaceNavigationMenu(defaultWorkspaceId)
    : handleNavigationMenu;

  const options = useWorkspaceOptions();
  const rememberedWorkspaceId = useRememberedWorkspaceId(currentWorkspaceId);
  const backWorkspace =
    options.find((option) => option.id === rememberedWorkspaceId) ?? options[0];

  return (
    <AppNavigation
      currentWorkspaceId={currentWorkspaceId}
      backHref={backWorkspace?.href}
    >
      {navigationMenu}
    </AppNavigation>
  );
};

const navigationHandle = z.object({
  workspaceId: workspaceIdSchema.optional(),
  navigationMenu: z.custom<ReactNode>().optional(),
  // The `/` route resolves its workspace indicator and menu from the default
  // workspace at render time instead of pinning a static one.
  defaultWorkspace: z.boolean().optional(),
});

type NavigationHandle = z.infer<typeof navigationHandle>;

export function checkIsMatchWithNavigation<T extends UIMatch>(
  match: T,
): match is T & {
  handle: NavigationHandle;
} {
  const handle = match.handle;
  return navigationHandle.safeParse(handle).success;
}
